import React, { useState, useMemo, useEffect } from 'react';
import { Box, TextField, Dialog, DialogContent, IconButton, AppBar, Toolbar, Typography, Tabs, Tab } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Image } from '../../types';
import { useLang } from '../../contexts/langContext';
import ImageListComponent from './ImageList';

interface ImagePickerProps {
  open: boolean;
  onClose: () => void;
  onSelectImage: (image: Image) => void;
  projectImages?: Image[];
  userImages?: Image[];
  publicImages?: Image[];
}

function ImagePicker({
  open,
  onClose,
  onSelectImage,
  projectImages = [],
  userImages = [],
  publicImages = [],
}: ImagePickerProps) {
  const { lang } = useLang();

  // Build available tabs dynamically (non-empty only)
  const tabs = useMemo(() => {
    const result: { key: 'Project' | 'User' | 'Public'; label: string; images: Image[] }[] = [];
    if (projectImages.length) {
      result.push({ key: 'Project', label: lang === 'en' ? 'Project' : '项目', images: projectImages });
    }
    if (userImages.length) {
      result.push({ key: 'User', label: lang === 'en' ? 'My Images' : '我的图片', images: userImages });
    }
    if (publicImages.length) {
      result.push({ key: 'Public', label: lang === 'en' ? 'Public' : '公共', images: publicImages });
    }
    return result;
  }, [projectImages, userImages, publicImages, lang]);

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  React.useEffect(() => {
    setActiveTab(0);
  }, [open, tabs.length]);

  // Filtered images based on active tab & search term
  const filteredImages = useMemo(() => {
    if (!tabs.length) return [] as Image[];
    const currentImages = tabs[activeTab]?.images || [];
    if (!searchTerm.trim()) return currentImages;
    const lower = searchTerm.toLowerCase();
    return currentImages.filter(img =>
      img.alt.toLowerCase().includes(lower) ||
      img.tagIds?.some(tagId => tagId.toLowerCase().includes(lower))
    );
  }, [tabs, activeTab, searchTerm]);

  const handleImageSelect = (image: Image) => {
    onSelectImage(image);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {lang === 'en' ? 'Select an Image' : '选择图片'}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        {tabs.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            {tabs.map(tab => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        )}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={lang === 'en' ? 'Search by description or tags' : '按描述或标签搜索'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        <ImageListComponent
          images={filteredImages}
          isUserImages={tabs[activeTab]?.key === 'User'}
          onImageSelect={(id) => {
            // Convert id to full image object for callback
            const img = (tabs[activeTab]?.images || []).find(im => im._id === id);
            if (img) handleImageSelect(img);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default ImagePicker;
